import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent, SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'

class OracleLoadRaster extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'oracle.loadraster',
        group: 'Oracle',
        title: 'Load Raster (Oracle)',
        description: dedent`
          Loads a raster file (GeoTIFF) from a URL into Oracle GeoRaster tables.
          Automatically creates the required metadata and RDT (Raster Data Table) structure.
          Optionally builds pyramids, computes spatial extent, and creates spatial indexes.
        `,
        starred: true
      },
      [
        // Basic parameters
        {
          name: 'sourceUrl',
          type: WorkflowComponentParamType.String,
          title: 'Source URL',
          placeholder: 'https://storage.example.com/raster/sentinel2_red.tif',
          description: 'HTTPS URL to the GeoTIFF file to load'
        },
        {
          name: 'bandName',
          type: WorkflowComponentParamType.String,
          title: 'Band name',
          placeholder: 'RED',
          optional: true,
          description: 'Band identifier (e.g., RED, NIR, BLUE). Leave empty to load all bands.'
        },

        // Advanced parameters
        {
          name: 'credentialName',
          type: WorkflowComponentParamType.String,
          title: 'Credential name',
          optional: true,
          advanced: true,
          placeholder: 'OCI_NATIVE_CRED',
          description: 'Credential for accessing private URLs. Leave empty for public URLs.'
        },
        {
          name: 'compression',
          type: WorkflowComponentParamType.Selection,
          title: 'Compression type',
          options: ['DEFLATE', 'JPEG', 'NONE'],
          default: 'DEFLATE',
          advanced: true
        },

        // Processing options
        {
          name: 'buildPyramids',
          type: WorkflowComponentParamType.Boolean,
          title: 'Build pyramids',
          default: true,
          description: 'Create pyramid levels for faster rendering at different scales'
        },
        {
          name: 'maxLevel',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum pyramid level',
          default: 5,
          showIf: [{ parameter: 'buildPyramids', value: true }],
          advanced: true
        },
        {
          name: 'resampling',
          type: WorkflowComponentParamType.Selection,
          title: 'Resampling method',
          options: ['NN', 'BILINEAR', 'CUBIC'],
          default: 'NN',
          showIf: [{ parameter: 'buildPyramids', value: true }],
          advanced: true
        },

        // Metadata options
        {
          name: 'registerMetadata',
          type: WorkflowComponentParamType.Boolean,
          title: 'Register metadata',
          default: true,
          description: 'Register spatial reference and extent'
        },
        {
          name: 'srid',
          type: WorkflowComponentParamType.Number,
          title: 'SRID',
          default: 4326,
          showIf: [{ parameter: 'registerMetadata', value: true }],
          description: 'Spatial Reference ID (e.g., 4326 for WGS84)'
        },
        {
          name: 'createSpatialIndex',
          type: WorkflowComponentParamType.Boolean,
          title: 'Create spatial index',
          default: true,
          advanced: true,
          description: 'Create spatial index on extent for faster queries'
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const prepParams = this._prepareParameters(params)

    // Generate workflow-specific table names
    const outputTableName = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    const metaTable = outputTableName
    const rdtTable = `${outputTableName}_RDT`

    // Build the PL/SQL block
    // Note: This component uses DBMS_CLOUD.GET_OBJECT for downloading, which is available in Oracle Autonomous Database.
    // The actual raster loading with SDO_GEOR.importFrom requires Java to be installed in the Oracle database.
    let query = dedent`
      DECLARE
        v_geor SDO_GEORASTER;
        v_georid NUMBER;
        v_url VARCHAR2(4000) := '${prepParams.sourceUrl}';
        v_blob BLOB;
        v_file UTL_FILE.FILE_TYPE;
        v_temp_file VARCHAR2(100);
        v_band_name VARCHAR2(50) := ${prepParams.bandName ? `'${prepParams.bandName}'` : 'NULL'};
        v_buffer RAW(32767);
        v_amount BINARY_INTEGER := 32767;
        v_pos INTEGER := 1;
        v_blob_len INTEGER;
      BEGIN
        -- Generate unique temp file name
        v_temp_file := 'temp_' || TO_CHAR(SYSTIMESTAMP, 'YYYYMMDDHH24MISS') || '_' ||
                       DBMS_RANDOM.STRING('X', 8) || '.tif';

        -- Drop existing tables if they exist
        BEGIN
          EXECUTE IMMEDIATE 'DROP TABLE ${metaTable} CASCADE CONSTRAINTS';
        EXCEPTION
          WHEN OTHERS THEN NULL;
        END;

        BEGIN
          EXECUTE IMMEDIATE 'DROP TABLE ${rdtTable} CASCADE CONSTRAINTS';
        EXCEPTION
          WHEN OTHERS THEN NULL;
        END;

        -- Create metadata table
        EXECUTE IMMEDIATE 'CREATE TABLE ${metaTable} (
          GEORID NUMBER PRIMARY KEY,
          BAND_NAME VARCHAR2(50),
          SOURCE_URL VARCHAR2(1000),
          LOADED_AT TIMESTAMP,
          RASTER SDO_GEORASTER,
          SPATIAL_EXTENT SDO_GEOMETRY
        )';

        -- Create RDT table
        EXECUTE IMMEDIATE 'CREATE TABLE ${rdtTable} OF SDO_RASTER (
          PRIMARY KEY (RASTERID, PYRAMIDLEVEL, BANDBLOCKNUMBER,
                      ROWBLOCKNUMBER, COLUMNBLOCKNUMBER)
        ) LOB(RASTERBLOCK) STORE AS SECUREFILE (COMPRESS ${prepParams.compression === 'NONE' ? 'NO' : 'HIGH'})';

        -- Download file using DBMS_CLOUD.GET_OBJECT (for Oracle Autonomous Database)
        -- This is more reliable and supports larger files than UTL_HTTP
        v_blob := DBMS_CLOUD.GET_OBJECT(
          credential_name => ${prepParams.credentialName ? `'${prepParams.credentialName}'` : 'NULL'},
          object_uri => v_url
        );

        v_blob_len := DBMS_LOB.GETLENGTH(v_blob);

        -- Write BLOB to file
        v_file := UTL_FILE.FOPEN('DATA_PUMP_DIR', v_temp_file, 'wb', 32767);

        WHILE v_pos <= v_blob_len LOOP
          IF v_pos + v_amount > v_blob_len THEN
            v_amount := v_blob_len - v_pos + 1;
          END IF;

          DBMS_LOB.READ(v_blob, v_amount, v_pos, v_buffer);
          UTL_FILE.PUT_RAW(v_file, v_buffer, TRUE);
          v_pos := v_pos + v_amount;
        END LOOP;

        UTL_FILE.FCLOSE(v_file);

        -- Free the BLOB
        IF DBMS_LOB.ISTEMPORARY(v_blob) = 1 THEN
          DBMS_LOB.FREETEMPORARY(v_blob);
        END IF;

        -- Get next GEORID
        v_georid := 1; -- Start at 1 for new table

        -- Initialize GeoRaster object
        v_geor := SDO_GEOR.INIT('${rdtTable}', v_georid);

        -- Insert metadata record using dynamic SQL
        EXECUTE IMMEDIATE 'INSERT INTO ${metaTable} (
          GEORID, BAND_NAME, SOURCE_URL, LOADED_AT, RASTER
        ) VALUES (
          :1, :2, :3, :4, :5
        )' USING v_georid, NVL(v_band_name, 'ALL'), '${prepParams.sourceUrl}', SYSTIMESTAMP, v_geor;

        -- Select for update to lock the row using dynamic SQL
        EXECUTE IMMEDIATE 'SELECT RASTER FROM ${metaTable} WHERE GEORID = :1 FOR UPDATE'
          INTO v_geor
          USING v_georid;

        -- Load the raster using importFrom
        SDO_GEOR.importFrom(
          georaster => v_geor,
          storageParam => 'blocksize=(512,512) interleaving=BSQ compression=${prepParams.compression}',
          r_sourceFormat => 'TIFF',
          r_sourceType => 'file',
          r_sourceName => 'DATA_PUMP_DIR/' || v_temp_file
        );
    `

    // Add pyramid generation if requested
    if (prepParams.buildPyramids) {
      query += dedent`

        -- Generate pyramids
        SDO_GEOR.generatePyramid(
          v_geor,
          'rLevel=${prepParams.maxLevel} resampling=${prepParams.resampling}'
        );

        -- Update the table with pyramid-enabled raster using dynamic SQL
        EXECUTE IMMEDIATE 'UPDATE ${metaTable} SET RASTER = :1 WHERE GEORID = :2'
          USING v_geor, v_georid;
      `
    }

    // Add metadata registration if requested
    if (prepParams.registerMetadata) {
      query += dedent`

        -- Set SRID
        SDO_GEOR.setModelSRID(v_geor, ${prepParams.srid});

        -- Update the metadata record with the modified GeoRaster using dynamic SQL
        -- Note: Spatial extent generation may not be available in all Oracle versions
        EXECUTE IMMEDIATE 'UPDATE ${metaTable} SET RASTER = :1 WHERE GEORID = :2'
          USING v_geor, v_georid;
      `
    }

    query += dedent`

        -- Clean up temp file
        UTL_FILE.FREMOVE('DATA_PUMP_DIR', v_temp_file);

        COMMIT;
        DBMS_OUTPUT.PUT_LINE('Raster loaded successfully from URL');
      EXCEPTION
        WHEN OTHERS THEN
          -- Clean up on error
          IF UTL_FILE.IS_OPEN(v_file) THEN
            UTL_FILE.FCLOSE(v_file);
          END IF;

          -- Try to clean up temp file
          BEGIN
            UTL_FILE.FREMOVE('DATA_PUMP_DIR', v_temp_file);
          EXCEPTION
            WHEN OTHERS THEN NULL;
          END;

          -- Free BLOB if it exists
          IF DBMS_LOB.ISTEMPORARY(v_blob) = 1 THEN
            DBMS_LOB.FREETEMPORARY(v_blob);
          END IF;

          ROLLBACK;
          RAISE_APPLICATION_ERROR(-20001,
            'Failed to load raster: ' || SQLERRM ||
            ' (Note: SDO_GEOR.importFrom requires Java in Oracle DB)');
      END;
    `

    return {
      query,
      outputs: { [SINGLE_OUTPUT_NAME]: metaTable }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema(): TableSchema {
    // The output table has a fixed schema with the columns created by the PL/SQL block
    return [
      {
        name: 'GEORID',
        type: ColumnDataType.Number
      },
      {
        name: 'BAND_NAME',
        type: ColumnDataType.String
      },
      {
        name: 'SOURCE_URL',
        type: ColumnDataType.String
      },
      {
        name: 'LOADED_AT',
        type: ColumnDataType.Timestamp
      },
      {
        name: 'RASTER',
        type: ColumnDataType.Binary
      },
      {
        name: 'SPATIAL_EXTENT',
        type: ColumnDataType.Geometry
      }
    ]
  }

  supportedProviders(): Provider[] {
    return [Provider.Oracle]
  }
}

export { OracleLoadRaster }
